import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Firestore,
  Timestamp,
} from 'firebase/firestore';
import { initializeFirebase } from '../firebase';
import { useAuth } from './AuthContext';
import { UserStats, UserStatsContextType } from '../types';

const UserStatsContext = createContext<UserStatsContextType | undefined>(undefined);

export const useUserStats = (): UserStatsContextType => {
  const context = useContext(UserStatsContext);
  if (context === undefined) {
    throw new Error('useUserStats must be used within a UserStatsProvider');
  }
  return context;
};

interface UserStatsProviderProps {
  children: ReactNode;
}

const defaultStats: UserStats = {
  totalPracticeMinutes: 0,
  pronunciationSessions: 0,
  interviewSessions: 0,
  averagePronunciationScore: 0,
  averageInterviewScore: 0,
  lastPracticeDate: null,
  currentStreak: 0,
  longestStreak: 0,
  createdAt: new Date(),
  updatedAt: new Date(),
};

export const UserStatsProvider: React.FC<UserStatsProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [db, setDb] = useState<Firestore | null>(null);

  const sessionStartRef = useRef<Date | null>(null);
  const sessionTypeRef = useRef<'pronunciation' | 'interview' | null>(null);

  // Initialize Firestore
  useEffect(() => {
    const init = async () => {
      try {
        const { db: firestore } = await initializeFirebase();
        setDb(firestore);
      } catch (err) {
        console.error('Failed to initialize Firestore:', err);
        setError('Failed to initialize database');
      }
    };
    init();
  }, []);

  // Fetch user stats when user or db changes
  useEffect(() => {
    const fetchStats = async () => {
      if (!currentUser || !db) {
        setStats(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const userDocRef = doc(db, 'userStats', currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const data = userDoc.data();
          setStats({
            ...data,
            lastPracticeDate: data.lastPracticeDate?.toDate() || null,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          } as UserStats);
        } else {
          // Create default stats for new user
          const newStats = {
            ...defaultStats,
            createdAt: Timestamp.now(),
            updatedAt: Timestamp.now(),
          };
          await setDoc(userDocRef, newStats);
          setStats({
            ...defaultStats,
            createdAt: new Date(),
            updatedAt: new Date(),
          });
        }
      } catch (err) {
        console.error('Error fetching user stats:', err);
        setError('Failed to load user statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentUser, db]);

  const startSession = (type: 'pronunciation' | 'interview') => {
    sessionStartRef.current = new Date();
    sessionTypeRef.current = type;
  };

  const endSession = async (score?: number) => {
    if (!sessionStartRef.current || !sessionTypeRef.current || !currentUser || !db || !stats) {
      return;
    }

    const endTime = new Date();
    const durationMs = endTime.getTime() - sessionStartRef.current.getTime();
    const durationMinutes = Math.max(1, Math.round(durationMs / 60000));

    // Only count sessions longer than 10 seconds (to avoid accidental clicks)
    if (durationMs < 10000) {
      sessionStartRef.current = null;
      sessionTypeRef.current = null;
      return;
    }

    const sessionType = sessionTypeRef.current;
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const lastPractice = stats.lastPracticeDate ? new Date(stats.lastPracticeDate) : null;
    if (lastPractice) {
      lastPractice.setHours(0, 0, 0, 0);
    }

    // Calculate streak
    let newStreak = stats.currentStreak;
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (!lastPractice) {
      newStreak = 1;
    } else if (lastPractice.getTime() === yesterday.getTime()) {
      newStreak = stats.currentStreak + 1;
    } else if (lastPractice.getTime() < yesterday.getTime()) {
      newStreak = 1;
    }

    const longestStreak = Math.max(stats.longestStreak, newStreak);

    // Calculate new averages
    let newAvgPronunciation = stats.averagePronunciationScore;
    let newAvgInterview = stats.averageInterviewScore;
    let newPronunciationSessions = stats.pronunciationSessions;
    let newInterviewSessions = stats.interviewSessions;

    if (sessionType === 'pronunciation' && score !== undefined) {
      newPronunciationSessions += 1;
      newAvgPronunciation =
        (stats.averagePronunciationScore * stats.pronunciationSessions + score) /
        newPronunciationSessions;
    } else if (sessionType === 'interview' && score !== undefined) {
      newInterviewSessions += 1;
      newAvgInterview =
        (stats.averageInterviewScore * stats.interviewSessions + score) /
        newInterviewSessions;
    } else if (sessionType === 'pronunciation') {
      newPronunciationSessions += 1;
    } else {
      newInterviewSessions += 1;
    }

    const updatedStats: Partial<UserStats> = {
      totalPracticeMinutes: stats.totalPracticeMinutes + durationMinutes,
      pronunciationSessions: newPronunciationSessions,
      interviewSessions: newInterviewSessions,
      averagePronunciationScore: Math.round(newAvgPronunciation),
      averageInterviewScore: Math.round(newAvgInterview),
      lastPracticeDate: today,
      currentStreak: newStreak,
      longestStreak,
      updatedAt: new Date(),
    };

    try {
      const userDocRef = doc(db, 'userStats', currentUser.uid);
      await updateDoc(userDocRef, {
        ...updatedStats,
        lastPracticeDate: Timestamp.fromDate(today),
        updatedAt: Timestamp.now(),
      });

      setStats((prev) =>
        prev
          ? {
              ...prev,
              ...updatedStats,
            }
          : null
      );
    } catch (err) {
      console.error('Error updating user stats:', err);
    }

    sessionStartRef.current = null;
    sessionTypeRef.current = null;
  };

  const refreshStats = async () => {
    if (!currentUser || !db) return;

    setLoading(true);
    try {
      const userDocRef = doc(db, 'userStats', currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (userDoc.exists()) {
        const data = userDoc.data();
        setStats({
          ...data,
          lastPracticeDate: data.lastPracticeDate?.toDate() || null,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date(),
        } as UserStats);
      }
    } catch (err) {
      console.error('Error refreshing stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const value: UserStatsContextType = {
    stats,
    loading,
    error,
    startSession,
    endSession,
    refreshStats,
  };

  return (
    <UserStatsContext.Provider value={value}>
      {children}
    </UserStatsContext.Provider>
  );
};
